import React from "react";
import ReactDOM from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { AppContext } from "context";
import useLocalStorage from "hook/useLocalStorage";
import { useShallow } from "zustand/react/shallow";
import useStorePlayer from "store/useStorePlayer";
import useStoreModal from "store/useStoreModal";
import Avatar from "component/Ui/Avatar";
import SignupFlow, { SignupFlowHandle } from "./index";
import { Mode } from "./types";

const SignupModal = () => {
    const { t } = useTranslation();
    const { setToken, setNonce } = React.useContext(AppContext);
    const [, setStoredToken] = useLocalStorage("LASTDOTCARDS.USER_IDENTITY");
    const { avatar, username, hasSavedAccount } = useStorePlayer(useShallow((state) => ({
        avatar: state.player?.avatar ?? "",
        username: state.player?.username ?? "",
        hasSavedAccount: state.player?.hasSavedAccount ?? false,
    })));
    const isOpen = useStoreModal((state) => state.modals["signup"]?.isOpen ?? false);
    const initialMode = ((useStoreModal((state) => state.modals["signup"]?.data) as { mode?: Mode })?.mode ?? "register") as Mode;
    const close = () => useStoreModal.getState().close("signup");
    const [mode, setMode] = React.useState<Mode>("register");
    const [currentStep, setCurrentStep] = React.useState("email");
    const flowRef = React.useRef<SignupFlowHandle>(null);

    React.useEffect(() => {
        if(isOpen) {
            setMode(initialMode);
            flowRef.current?.reset();
        }
    }, [isOpen, initialMode]);

    const handleRestore = (token: string) => {
        setStoredToken(token);
        setToken(token);
        setNonce(Math.random());
        close();
    };

    return ReactDOM.createPortal(
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    key="signup-modal"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="fixed inset-0 z-[401] flex items-end md:items-center md:justify-center"
                >
                    <div className="absolute inset-0 bg-black/80" onClick={close} />

                    <motion.div
                        initial={{ y: "100%" }}
                        animate={{ y: 0 }}
                        exit={{ y: "100%" }}
                        transition={{ type: "spring", damping: 30, stiffness: 300 }}
                        className="relative z-10 w-full md:max-w-lg rounded-t-2xl md:rounded-2xl border border-white/5 shadow-2xl shadow-black/50 overflow-hidden max-h-[95dvh] md:max-h-[85vh] overflow-y-auto panel-scrollbar"
                        style={{ background: "linear-gradient(180deg, rgba(25,25,35,0.97) 0%, rgba(15,15,22,0.99) 100%)" }}
                    >
                        {/* Close */}
                        <button
                            onClick={close}
                            className="absolute top-4 right-4 z-20 w-8 h-8 rounded-full bg-white/5 hover:bg-white/10 flex items-center justify-center cursor-pointer transition-colors"
                        >
                            <svg className="w-4 h-4 text-white/50" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>

                        <div className="px-6 md:px-10 pt-8 pb-10 md:pb-8 flex flex-col items-center gap-5">
                            {/* Pill toggle + back */}
                            <div className="flex items-center gap-2 self-start">
                                <AnimatePresence>
                                    {currentStep !== "email" && (
                                        <motion.button
                                            initial={{ opacity: 0, width: 0 }}
                                            animate={{ opacity: 1, width: 32 }}
                                            exit={{ opacity: 0, width: 0 }}
                                            transition={{ duration: 0.15 }}
                                            type="button"
                                            onClick={() => flowRef.current?.goBack()}
                                            className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/15 flex items-center justify-center cursor-pointer transition-colors shrink-0 overflow-hidden"
                                        >
                                            <svg className="w-4 h-4 text-white/60" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                                            </svg>
                                        </motion.button>
                                    )}
                                </AnimatePresence>
                            {!hasSavedAccount && (
                            <div className="flex items-center gap-1 p-1 rounded-full bg-white/10 border border-white/10">
                                {(["register", "login"] as const).map((tab) => (
                                    <button
                                        key={tab}
                                        type="button"
                                        onClick={() => { setMode(tab); flowRef.current?.reset(); }}
                                        className={`relative px-3.5 py-1.5 rounded-full font-changa-one text-xs tracking-[0.1em] uppercase cursor-pointer transition-colors ${
                                            mode === tab ? "text-white" : "text-white/40 hover:text-white/60"
                                        }`}
                                    >
                                        {mode === tab && (
                                            <motion.div
                                                layoutId="signup-modal-pill"
                                                className="absolute inset-0 rounded-full bg-white/15"
                                                transition={{ type: "spring", stiffness: 400, damping: 30 }}
                                            />
                                        )}
                                        <span className="relative z-10">{t(`signup.tab_${tab}`)}</span>
                                    </button>
                                ))}
                            </div>
                            )}
                            </div>

                            <div className="my-4 w-full">
                                {/* Flow */}
                                <SignupFlow
                                    ref={flowRef}
                                    theme="dark"
                                    mode={mode}
                                    showNonCustodial
                                    onComplete={close}
                                    onRestore={handleRestore}
                                    onStepChange={setCurrentStep}
                                    className="w-full text-white"
                                    header={
                                        <div className="flex flex-col items-center gap-2 w-full">
                                            {mode === "register" ? (
                                                <div className="flex items-center justify-center gap-x-2 gap-y-0 flex-wrap">
                                                    <span className="text-white font-changa-one text-lg tracking-wide">
                                                        {t("signup.modal.register.title_prefix")}
                                                    </span>
                                                    {username && (
                                                        <span className="inline-flex items-center gap-1.5 bg-white/10 border border-white/10 rounded-full pl-1.5 pr-3 py-1">
                                                            <Avatar src={avatar} className="w-5 h-5" />
                                                            <span className="text-white font-changa-one text-xs">{username}</span>
                                                        </span>
                                                    )}
                                                </div>
                                            ) : (
                                                <p className="text-white font-changa-one text-lg tracking-wide text-center">
                                                    {t("signup.modal.login.title")}
                                                </p>
                                            )}
                                            <p className="text-white/50 text-sm text-center leading-relaxed">
                                                {t(`signup.modal.${mode}.subtitle`)}
                                            </p>
                                        </div>
                                    }
                                />
                            </div>

                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>,
        document.body
    );
};

export default SignupModal;
